import { get } from 'lodash';
import { FlatOrgUserData, FlatOrgUserTreeTableNode } from 'src/utils/treeNode';

type SortOrder = 0 | 1 | -1 | null | undefined;

export const customSortFunction = (
  data: FlatOrgUserTreeTableNode[],
  sortField: string,
  sortOrder: SortOrder,
): FlatOrgUserTreeTableNode[] => {
  return [...data].sort((a, b) => {
    if (a.data.recordType !== b.data.recordType) {
      return a.data.recordType === 'W' ? -1 : 1;
    }

    if (sortField && sortOrder !== 0 && sortOrder !== null && sortOrder !== undefined) {
      let valueA = get(a.data, sortField);
      let valueB = get(b.data, sortField);

      if (valueA === undefined) return 1;
      if (valueB === undefined) return -1;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder * valueA.localeCompare(valueB);
      }

      return sortOrder * (valueA - valueB);
    }

    return 0;
  });
};

export const applyCustomSort = (
  dataArray: FlatOrgUserTreeTableNode[],
  sortField: string,
  sortOrder: SortOrder,
): FlatOrgUserTreeTableNode[] => {
  const sortedData = customSortFunction(dataArray, sortField, sortOrder);

  return sortedData.map((node) => {
    if (node.children && node.children.length > 0) {
      return { ...node, children: applyCustomSort(node.children, sortField, sortOrder) };
    }
    return node;
  });
};

// Filter tree by search term (case-insensitive, mid-word match on name)
export const filterData = (nodes: FlatOrgUserTreeTableNode[], term: string): FlatOrgUserTreeTableNode[] => {
  const trimmedTerm = term.trim();
  if (!trimmedTerm) return JSON.parse(JSON.stringify(nodes));

  const normalizedTerm = trimmedTerm.toLowerCase();
  const matchesNode = (node: FlatOrgUserTreeTableNode) => node.data.name?.toLowerCase().includes(normalizedTerm);

  return nodes
    .filter((node) => {
      if (matchesNode(node)) return true;

      if (node.children && node.children.length > 0) {
        const filteredChildren = filterData(node.children, term);
        if (filteredChildren.length) {
          return true;
        }
      }

      return false;
    })
    .map((node) => {
      if (node.children && node.children.length > 0) {
        // If this node matches, keep all children
        if (matchesNode(node)) {
          return {
            ...node,
            children: JSON.parse(JSON.stringify(node.children)),
          };
        }

        // Otherwise, only keep matching children
        const filteredChildren = filterData(node.children, term);
        return {
          ...node,
          children: filteredChildren,
        };
      }
      return node;
    });
};

// Build level lookup map from filtered tree data
export const buildLevelById = (filteredData: FlatOrgUserTreeTableNode[]): Map<string, number> => {
  const result = new Map<string, number>();

  const traverse = (nodes: FlatOrgUserTreeTableNode[]) => {
    for (const node of nodes) {
      result.set(node.data.id, node.level);
      if (node.children?.length) {
        traverse(node.children);
      }
    }
  };

  traverse(filteredData);
  return result;
};

// Build parent org name lookup map
export const buildParentOrgNameById = (data: FlatOrgUserData[]): Map<string, string | null> => {
  const byId = new Map<string, FlatOrgUserData>(data.map((item) => [item.id, item]));
  const result = new Map<string, string | null>();

  const normalizeParentName = (parentName?: string | null) => {
    const normalized = parentName?.trim();
    if (!normalized || normalized.toLowerCase() === 'cydea') {
      return null;
    }
    return normalized;
  };

  const findParentOrg = (startId?: string | null) => {
    let currentId = startId ?? null;
    const visited = new Set<string>();

    while (currentId) {
      if (visited.has(currentId)) break;
      visited.add(currentId);

      const parent = byId.get(currentId);
      if (!parent) break;

      if (parent.recordType === 'O') {
        return parent;
      }

      currentId = parent.parentId ?? null;
    }

    return null;
  };

  data.forEach((item) => {
    if (item.recordType === 'W') {
      // For workspaces: get parent org of the workspace's org
      const orgParent = findParentOrg(item.parentId);
      const orgParentParent = orgParent ? findParentOrg(orgParent.parentId) : null;
      result.set(item.id, normalizeParentName(orgParentParent?.name));
      return;
    }

    // For orgs: get direct parent org
    const directOrgParent = findParentOrg(item.parentId);
    result.set(item.id, normalizeParentName(directOrgParent?.name));
  });

  return result;
};
